// System directives

'use client';

// System dependencies

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

// Application dependencies

import Navlink from '@/components/nav/public/navLink/Navlink';
import { AuthFields } from '@/_auth/interfaces/auth.interfaces';
import { RouteFields, RouteNames } from '@/interfaces/route.interfaces';
import { cn } from '@/lib/utils';
import { getAuthSelector } from '@/_auth/redux/auth/auth.slices';
import { manageFormSpinner } from '@/lib/functions';
import { useAppSelector } from '@/redux/store.hooks';

// Styling

import { BiDesktop, BiMenu, BiUser } from 'react-icons/bi';

const NavbarMenu = () => {
  // Application state

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdown = useRef<HTMLDivElement>(null);

  // Redux state

  const auth = useAppSelector(getAuthSelector);

  // ---------------------------------------------------------------------------------------------------------------------------------------
  // Form interaction and rendering
  // ---------------------------------------------------------------------------------------------------------------------------------------

  // Close on click outside

  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!dropdown.current) return;
      if (!dropdownOpen || dropdown.current.contains(target as HTMLDivElement)) return;
      setDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  return (
    <div className="relative">
      {/* Hamburger Button */}
      <button
        aria-controls="sidebar"
        className="block bg-white p-1.5 dark:bg-zinc-800 md:hidden"
        onClick={(e) => {
          e.stopPropagation();
          setDropdownOpen(!dropdownOpen);
        }}>
        <BiMenu className="h-[28px] w-[28px]" />
      </button>
      {/* Dropdown Menu */}
      <div
        className={cn('border-stroke dark:bg-boxdark dark:border-strokedark absolute right-0 mt-3 flex w-62.5 flex-col border-1 bg-white shadow-default dark:bg-zinc-800', dropdownOpen === true ? 'block' : 'hidden')}
        onBlur={() => setDropdownOpen(false)}
        onFocus={() => setDropdownOpen(true)}
        ref={dropdown}>
        {/* User options */}
        <ul className="mt-4 flex flex-col gap-5 border-0 px-6 pb-4">
          <li>
            {!auth[AuthFields.User] ? (
              <div onClick={() => manageFormSpinner(`loading ${RouteNames[RouteFields.SignIn].name} page - please wait`)}>
                <Link className="flex items-center gap-3.5 text-sm font-medium duration-300 ease-in-out hover:text-logoblue dark:hover:text-logoblue-300 lg:text-base" href={RouteNames[RouteFields.SignIn].route}>
                  <BiUser className="h-[22px] w-[22px] fill-current" />
                  {RouteNames[RouteFields.SignIn].name}
                </Link>
              </div>
            ) : (
              <div onClick={() => manageFormSpinner(`loading ${RouteNames[RouteFields.Dashboard].name} page - please wait`)}>
                <Link className="flex items-center gap-3.5 text-sm font-medium duration-300 ease-in-out hover:text-googleblue lg:text-base" href={RouteNames[RouteFields.Dashboard].route}>
                  <BiDesktop className="h-[22px] w-[22px] fill-current" />
                  {RouteNames[RouteFields.Dashboard].name}
                </Link>
              </div>
            )}
          </li>
        </ul>
      </div>
      {/* Standard Menu */}
      <div className="hidden items-center space-x-2 md:flex lg:order-2">
        {!auth[AuthFields.User] ? <Navlink href={RouteNames[RouteFields.SignIn].route} label={RouteNames[RouteFields.SignIn].name} /> : <Navlink href={RouteNames[RouteFields.Dashboard].route} label={RouteNames[RouteFields.Dashboard].name} />}
      </div>
    </div>
  );
};

export default NavbarMenu;
